import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui'
import { useAuthStore } from '@/store/auth'

const navLinks = [
  { href: '/', label: 'Dashboard' },
  { href: '/browse', label: 'Browse' },
  { href: '/search', label: 'Search' },
]

export function Navigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const { user, isAuthenticated, logout } = useAuthStore()

  const handleLogout = async () => {
    await logout()
    navigate('/')
    setIsUserMenuOpen(false)
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <div className="glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <JellyfishLogo />
              <span className="font-heading font-bold text-xl text-gradient-primary">
                SEO Workflows
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                    location.pathname === link.href
                      ? 'text-primary-accent bg-primary-accent/10'
                      : 'text-neutral-400 hover:text-neutral-100 hover:bg-white/5'
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right side */}
            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated && user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-accent to-primary-glow flex items-center justify-center text-white text-sm font-medium">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-neutral-100 text-sm">{user.name}</span>
                    <svg
                      className={cn(
                        'w-4 h-4 text-neutral-400 transition-transform',
                        isUserMenuOpen && 'rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* User dropdown menu */}
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-48 glass-card rounded-xl overflow-hidden"
                    >
                      <div className="p-2">
                        <Link
                          to={`/user/${user.id}`}
                          onClick={() => setIsUserMenuOpen(false)}
                          className="block px-3 py-2 rounded-lg text-sm text-neutral-300 hover:text-neutral-100 hover:bg-white/5"
                        >
                          Your Profile
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="block px-3 py-2 rounded-lg text-sm text-neutral-300 hover:text-neutral-100 hover:bg-white/5"
                        >
                          Settings
                        </Link>
                        <hr className="my-2 border-white/10" />
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm text-secondary-pink hover:bg-secondary-pink/10"
                        >
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="sm">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-neutral-400 hover:text-neutral-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="md:hidden glass border-b border-white/5"
        >
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(
                  'block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                  location.pathname === link.href
                    ? 'text-primary-accent bg-primary-accent/10'
                    : 'text-neutral-400 hover:text-neutral-100 hover:bg-white/5'
                )}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 flex flex-col gap-2">
              {isAuthenticated && user ? (
                <>
                  <Link
                    to={`/user/${user.id}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2 rounded-lg text-sm text-neutral-300 hover:text-neutral-100 hover:bg-white/5"
                  >
                    Your Profile
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2 rounded-lg text-sm text-neutral-300 hover:text-neutral-100 hover:bg-white/5"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 rounded-lg text-sm text-secondary-pink hover:bg-secondary-pink/10"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-center">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button className="w-full justify-center">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </nav>
  )
}

function JellyfishLogo() {
  return (
    <motion.svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      animate={{ y: [0, -2, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      {/* Jellyfish bell/hood */}
      <defs>
        <linearGradient id="bellGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00D9FF" />
          <stop offset="100%" stopColor="#7B61FF" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="1" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Main bell */}
      <ellipse
        cx="16"
        cy="11"
        rx="10"
        ry="8"
        fill="url(#bellGradient)"
        opacity="0.9"
        filter="url(#glow)"
      />

      {/* Inner highlight */}
      <ellipse
        cx="16"
        cy="10"
        rx="6"
        ry="4"
        fill="white"
        opacity="0.2"
      />

      {/* Tentacles */}
      <motion.path
        d="M8 16 Q6 20 8 24 Q10 28 8 30"
        stroke="#00D9FF"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
        animate={{ d: ['M8 16 Q6 20 8 24 Q10 28 8 30', 'M8 16 Q10 20 8 24 Q6 28 8 30'] }}
        transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
      />
      <motion.path
        d="M12 17 Q10 22 12 26 Q14 30 12 32"
        stroke="#7B61FF"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
        animate={{ d: ['M12 17 Q10 22 12 26 Q14 30 12 32', 'M12 17 Q14 22 12 26 Q10 30 12 32'] }}
        transition={{ duration: 2.2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: 0.2 }}
      />
      <motion.path
        d="M16 18 Q14 23 16 27 Q18 31 16 32"
        stroke="#00D9FF"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
        animate={{ d: ['M16 18 Q14 23 16 27 Q18 31 16 32', 'M16 18 Q18 23 16 27 Q14 31 16 32'] }}
        transition={{ duration: 1.8, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: 0.4 }}
      />
      <motion.path
        d="M20 17 Q22 22 20 26 Q18 30 20 32"
        stroke="#7B61FF"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
        animate={{ d: ['M20 17 Q22 22 20 26 Q18 30 20 32', 'M20 17 Q18 22 20 26 Q22 30 20 32'] }}
        transition={{ duration: 2.4, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: 0.6 }}
      />
      <motion.path
        d="M24 16 Q26 20 24 24 Q22 28 24 30"
        stroke="#00D9FF"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
        animate={{ d: ['M24 16 Q26 20 24 24 Q22 28 24 30', 'M24 16 Q22 20 24 24 Q26 28 24 30'] }}
        transition={{ duration: 2.1, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: 0.8 }}
      />
    </motion.svg>
  )
}
